import { useState, useEffect, useMemo } from "react";
import { ConfirmDestroyRoomsModal } from "./ConfirmDestroyRoomsModal";
import { AdminSupportChat } from "./AdminSupportChat";
import { getSupabaseClient } from "../../lib/supabase";

const brandPrimary = "#2C2731";
const brandSurface = "#FFFCEF";
const brandSurfaceAlt = "#F2EAC3";

type Tab = "support" | "rooms";

type SupportConversation = {
  id: string;
  user_id: string;
  user_role: string;
  admin_unread_count: number;
  last_message_at: string | null;
  last_message_preview: string | null;
  user_name?: string;
};

const pageStyle: React.CSSProperties = {
  padding: "24px",
  maxWidth: "1200px",
  margin: "0 auto",
};

const cardStyle: React.CSSProperties = {
  border: `1px solid ${brandPrimary}`,
  borderRadius: 16,
  padding: 24,
  background: brandSurface,
  boxShadow: "0 2px 8px rgba(44,39,49,0.1)",
};

const titleStyle: React.CSSProperties = {
  fontSize: 24,
  fontWeight: 600,
  color: brandPrimary,
  marginBottom: 16,
};

const descriptionStyle: React.CSSProperties = {
  fontSize: 14,
  color: brandPrimary,
  lineHeight: 1.6,
  marginBottom: 24,
};

const buttonStyle: React.CSSProperties = {
  padding: "12px 24px",
  borderRadius: 8,
  border: `1px solid ${brandPrimary}`,
  background: brandPrimary,
  color: brandSurface,
  fontSize: 14,
  fontWeight: 600,
  cursor: "pointer",
  transition: "all 0.2s ease",
};

const warningBoxStyle: React.CSSProperties = {
  border: "1px solid #D97706",
  borderRadius: 8,
  padding: 16,
  background: "#FEF3C7",
  marginBottom: 24,
};

const warningTitleStyle: React.CSSProperties = {
  fontSize: 14,
  fontWeight: 600,
  color: "#92400E",
  marginBottom: 8,
};

const warningTextStyle: React.CSSProperties = {
  fontSize: 13,
  color: "#92400E",
  lineHeight: 1.5,
};

const tabStyle = (active: boolean): React.CSSProperties => ({
  padding: "10px 20px",
  borderRadius: "8px 8px 0 0",
  border: `1px solid ${brandPrimary}`,
  borderBottom: active ? `2px solid ${brandSurface}` : `1px solid ${brandPrimary}`,
  background: active ? brandSurface : "transparent",
  color: brandPrimary,
  fontSize: 14,
  fontWeight: active ? 600 : 400,
  cursor: "pointer",
  marginBottom: -1,
  position: "relative",
});

function formatTimeAgo(dateStr: string | null): string {
  if (!dateStr) return "";
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 1) return "just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  const diffDays = Math.floor(diffHours / 24);
  return `${diffDays}d ago`;
}

export function MessagesPage() {
  const [activeTab, setActiveTab] = useState<Tab>("support");
  const [showModal, setShowModal] = useState(false);
  const [conversations, setConversations] = useState<SupportConversation[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedConversation, setSelectedConversation] = useState<SupportConversation | null>(null);

  const supabase = useMemo(() => getSupabaseClient(), []);

  const loadConversations = async (showSpinner = false) => {
    try {
      if (showSpinner) setLoading(true);

      // Fetch all support conversations (admin can see all via RLS)
      const { data: convos, error } = await supabase
        .from("support_conversations")
        .select("*")
        .order("last_message_at", { ascending: false, nullsFirst: false });

      if (error) {
        console.error("Error fetching support conversations:", error);
        return;
      }

      if (!convos || convos.length === 0) {
        setConversations([]);
        return;
      }

      // Fetch user names for all conversations
      const userIds = convos.map((c: SupportConversation) => c.user_id);
      const { data: profiles } = await supabase
        .from("profiles")
        .select("id, full_name")
        .in("id", userIds);

      const nameMap = new Map(
        (profiles || []).map((p: { id: string; full_name: string | null }) => [p.id, p.full_name])
      );

      const enriched = convos.map((c: SupportConversation) => ({
        ...c,
        user_name: nameMap.get(c.user_id) || "Unknown User",
      }));

      setConversations(enriched);
    } catch (err) {
      console.error("Error loading conversations:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === "support") {
      loadConversations(true);
      // Poll for new/updated conversations every 10 seconds
      const intervalId = setInterval(() => loadConversations(false), 10000);
      return () => clearInterval(intervalId);
    }
  }, [activeTab]);

  const handleBackFromChat = () => {
    setSelectedConversation(null);
    loadConversations(false);
  };

  // Support Inbox tab
  const renderSupportInbox = () => {
    if (selectedConversation) {
      return (
        <AdminSupportChat
          targetUserId={selectedConversation.user_id}
          userName={selectedConversation.user_name || "Unknown User"}
          userRole={selectedConversation.user_role}
          onBack={handleBackFromChat}
        />
      );
    }

    if (loading) {
      return (
        <div style={{ padding: 40, textAlign: "center", color: "rgba(44,39,49,0.5)" }}>
          Loading conversations...
        </div>
      );
    }

    if (conversations.length === 0) {
      return (
        <div style={{ padding: 40, textAlign: "center", color: "rgba(44,39,49,0.5)" }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ display: "inline-block", opacity: 0.3 }}>
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
            </svg>
          </div>
          <div style={{ fontSize: 14 }}>No support conversations yet</div>
          <div style={{ fontSize: 13, marginTop: 4 }}>Conversations will appear here when guides or seekers message support.</div>
        </div>
      );
    }

    return (
      <div style={{ display: "flex", flexDirection: "column" }}>
        {conversations.map((convo) => {
          const hasUnread = convo.admin_unread_count > 0;
          const roleLabel = convo.user_role === "guide" ? "Guide" : "Seeker";
          return (
            <button
              key={convo.id}
              onClick={() => setSelectedConversation(convo)}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
                padding: "14px 16px",
                borderBottom: "1px solid rgba(44,39,49,0.1)",
                background: hasUnread ? "#F2EAC3" : "transparent",
                cursor: "pointer",
                border: "none",
                textAlign: "left",
                width: "100%",
                transition: "background 0.15s",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = hasUnread ? "#E8DFBD" : "#F9F5E0";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = hasUnread ? "#F2EAC3" : "transparent";
              }}
            >
              {/* Avatar */}
              <div style={{
                width: 40, height: 40, borderRadius: "50%", background: "#E8DFBD",
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 13, fontWeight: 600, color: brandPrimary, flexShrink: 0,
                border: "1px solid rgba(44,39,49,0.2)"
              }}>
                {(convo.user_name || "?").slice(0, 2).toUpperCase()}
              </div>

              {/* Info */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 2 }}>
                  <span style={{ fontWeight: hasUnread ? 700 : 600, fontSize: 14, color: brandPrimary }}>
                    {convo.user_name}
                  </span>
                  <span style={{
                    fontSize: 11, padding: "1px 6px", borderRadius: 4,
                    background: convo.user_role === "guide" ? brandPrimary : "#304031",
                    color: brandSurface, fontWeight: 500,
                  }}>
                    {roleLabel}
                  </span>
                  {convo.last_message_at && (
                    <span style={{ fontSize: 12, color: "rgba(44,39,49,0.5)", marginLeft: "auto" }}>
                      {formatTimeAgo(convo.last_message_at)}
                    </span>
                  )}
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{
                    fontSize: 13,
                    color: hasUnread ? brandPrimary : "rgba(44,39,49,0.6)",
                    fontWeight: hasUnread ? 500 : 400,
                    overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" as const,
                    flex: 1,
                  }}>
                    {convo.last_message_preview || "No messages yet"}
                  </span>
                  {hasUnread && (
                    <span style={{
                      background: brandPrimary, color: brandSurface, borderRadius: 10,
                      padding: "1px 7px", fontSize: 11, fontWeight: 700, flexShrink: 0,
                    }}>
                      {convo.admin_unread_count}
                    </span>
                  )}
                </div>
              </div>
            </button>
          );
        })}
      </div>
    );
  };

  // Room Management tab (existing content)
  const renderRoomManagement = () => (
    <>
      <div style={descriptionStyle}>
        <p style={{ marginBottom: 12 }}>
          This tool allows you to hide all existing chat messages from users. When you reset message rooms,
          all current conversations will be hidden and users will start with fresh chat rooms.
        </p>
        <p>
          <strong>Note:</strong> Messages cannot be permanently deleted as they are stored on the chat provider's server for 7 days from message send.
          This action will hide old messages from users by incrementing the room version, making them inaccessible through the UI.
        </p>
      </div>

      <div style={warningBoxStyle}>
        <div style={warningTitleStyle}>Important Information</div>
        <div style={warningTextStyle}>
          <ul style={{ margin: 0, paddingLeft: 20 }}>
            <li>All active chat conversations will be hidden from both guides and seekers</li>
            <li>Users will see empty chat rooms when they next log in</li>
            <li>Messages remain on the chat provider's server for 7 days but will not be accessible through the UI</li>
            <li>This action affects all users system-wide</li>
          </ul>
        </div>
      </div>

      <button
        style={buttonStyle}
        onClick={() => setShowModal(true)}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = brandSurfaceAlt;
          e.currentTarget.style.color = brandPrimary;
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = brandPrimary;
          e.currentTarget.style.color = brandSurface;
        }}
      >
        Reset All Message Rooms
      </button>
    </>
  );

  return (
    <div style={pageStyle}>
      <h1 style={titleStyle}>Messages</h1>

      {/* Tabs */}
      <div style={{ display: "flex", gap: 4, borderBottom: `1px solid ${brandPrimary}`, marginBottom: 0 }}>
        <button
          style={tabStyle(activeTab === "support")}
          onClick={() => { setActiveTab("support"); setSelectedConversation(null); }}
        >
          Support Inbox
          {conversations.some(c => c.admin_unread_count > 0) && (
            <span style={{
              marginLeft: 8, background: brandPrimary, color: brandSurface,
              borderRadius: 10, padding: "1px 6px", fontSize: 11, fontWeight: 700,
            }}>
              {conversations.reduce((sum, c) => sum + c.admin_unread_count, 0)}
            </span>
          )}
        </button>
        <button
          style={tabStyle(activeTab === "rooms")}
          onClick={() => setActiveTab("rooms")}
        >
          Room Management
        </button>
      </div>

      <div style={{
        ...cardStyle,
        ...(activeTab === "support" && selectedConversation ? { height: 600, padding: 0, overflow: "hidden" } : {}),
      }}>
        {activeTab === "support" ? renderSupportInbox() : renderRoomManagement()}
      </div>

      {showModal && (
        <ConfirmDestroyRoomsModal
          onClose={() => setShowModal(false)}
          onConfirm={() => {
            setShowModal(false);
          }}
        />
      )}
    </div>
  );
}
